import React, { useCallback, useImperativeHandle, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import CardTemplate from "components/card/card-template/CardTemplate";
import {
    AAAMemberField,
    FirstNameField,
    LastNameField,
    AddressField,
    CityField,
    StateField,
    ZipCodeField,
    PhoneNumberField,
    EmailField,
    PhoneTypeField,
    DateOfBirthField,
    GenderRadioField,
    TCPAConsentField,
} from "components/form-fields";
import { useQuote } from "hooks/useQuote";
import { useOffer } from "hooks/useOffer";
import { useContactInformation } from "hooks/useContactInformation";
import type {
    ContactInformationFormReturn,
    ContactInformationRef,
    ContactInformationValues,
    DTOApplicationFormReturn,
} from "types/Card";
import { ContactInformationStateSchema } from "components/card/contact-information/ContactInformationStateSchema";
import { toUsStates } from "constants/validations";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";
import { GIWLQuoteModal } from "components/modal/giwl-quote/GIWLQuoteModal";

interface ContactInformationProps {
    className?: string;
    form: ContactInformationFormReturn;
}

type ContactInformationWithRef = ContactInformationProps & {
    ref?: React.Ref<ContactInformationRef>;
};

/**
 * Populates form fields with data from offer/quote
 */
const populateFormFromQuoteData = (
    parseResult: ReturnType<typeof ContactInformationStateSchema.safeParse>,
    setValue: DTOApplicationFormReturn["setValue"],
    isMember: boolean
) => {
    if (!parseResult.success || !parseResult.data.application) return;

    const app = parseResult.data.application;

    if (isMember && app.firstName) {
        setValue("contactInformation.firstName", app.firstName);
    }
    if (isMember && app.lastName) {
        setValue("contactInformation.lastName", app.lastName);
    }

    if (app.address) {
        setValue("contactInformation.address", app.address);
    }
    if (app.city) {
        setValue("contactInformation.city", app.city);
    }
    // Set entire dateOfBirth object to avoid mutating frozen objects from Redux
    setValue("contactInformation.dateOfBirth", {
        month: app.month,
        day: app.day,
        year: app.year,
    });
    setValue("contactInformation.state", app.state);
    setValue("contactInformation.applicantType", app.applicantType);
    if (app.gender) {
        setValue("contactInformation.gender", app.gender);
    }
    if (app.zipCode) {
        setValue("contactInformation.zipCode", app.zipCode);
    }
};

/**
 * Populates form fields with saved data from Redux
 */
const populateFormFromReduxData = (
    contactDataRedux: ContactInformationValues | null | undefined,
    setValue: DTOApplicationFormReturn["setValue"]
) => {
    if (!contactDataRedux) return;

    setValue("contactInformation.firstName", contactDataRedux.firstName);
    setValue("contactInformation.lastName", contactDataRedux.lastName);
    setValue("contactInformation.email", contactDataRedux?.email);
    setValue("contactInformation.phone", contactDataRedux?.phone);
    setValue("contactInformation.phoneType", contactDataRedux?.phoneType);
    setValue("contactInformation.tcpaConsent", contactDataRedux?.tcpaConsent);
    setValue("contactInformation.address", contactDataRedux?.address);
    setValue("contactInformation.city", contactDataRedux?.city);
    setValue("contactInformation.zipCode", contactDataRedux?.zipCode);

    if (contactDataRedux.gender) {
        setValue("contactInformation.gender", contactDataRedux.gender);
    }
    if (contactDataRedux.aaaMemberNumber) {
        setValue("contactInformation.aaaMemberNumber", contactDataRedux.aaaMemberNumber);
    }
};
const useTCPAVisibility = (isMLTA: boolean, phoneType?: string) => {
    return isMLTA ? !!phoneType : phoneType === "Mobile" || phoneType === "Home";
};

const shouldResetTCPAConsent = (isMLTA: boolean, phoneType: string): boolean => {
    if (isMLTA) return false; // MLTA: Never reset (TCPA applies to all phone types)
    return phoneType === "Business"; // DTO: Reset only for Business
};
const ContactInformation = ({ ref, ...props }: ContactInformationWithRef) => {
    const { className = "", form } = props;
    // ========== Hooks ==========
    const navigate = useNavigate();
    const { quoteData } = useQuote();
    const { offerData } = useOffer();
    const { contactData } = useContactInformation();
    const editQuoteTriggerRef = useRef<HTMLButtonElement>(null);

    const typedForm = form as DTOApplicationFormReturn;
    const {
        register,
        formState: { errors },
        setValue,
        trigger,
        getValues,
    } = typedForm;

    // ========== State ==========
    const contactDataRedux = contactData?.contactInformation;
    const contactInformation = useWatch({
        control: typedForm.control as any,
        name: "contactInformation",
    });

    // ========== Computed Values ==========
    const state = useMemo(
        () => ({
            application: quoteData
                ? {
                      firstName: offerData?.firstName,
                      lastName: offerData?.lastName,
                      address: offerData?.address,
                      city: offerData?.city,
                      applicantType: quoteData.applicantType,
                      month: quoteData.dobMonth,
                      day: quoteData.dobDay,
                      year: quoteData.dobYear,
                      state: quoteData.state,
                      gender: quoteData.gender,
                      zipCode: quoteData.zipCode,
                  }
                : undefined,
        }),
        [quoteData, offerData]
    );

    const parseResult = useMemo(() => ContactInformationStateSchema.safeParse(state), [state]);
    const validatedState = parseResult.success ? parseResult.data : null;
    const applicationData = validatedState?.application;
    const isMember = applicationData?.applicantType === "member";
    const { isMLTA, isGIWL } = useProductContext();
    const useTCPA = isMLTA || isGIWL;
    const shouldShowTCPA = useTCPAVisibility(useTCPA, contactInformation?.phoneType);
    const disableNames = isMember && !isMLTA && !isGIWL;
    const showAaaMemberNumberSection = isMLTA && !offerData?.aaaMemberNumber;

    // ========== Effects ==========
    // Validate state
    useEffect(() => {
        if (!parseResult.success) {
            navigate(ROUTES.SOMETHING_WENT_WRONG);
        }
    }, [parseResult.success, parseResult.error, navigate]);

    // Restore form data from quote/offer
    useEffect(() => {
        populateFormFromQuoteData(parseResult, setValue, isMember);

        // Only populate zipCode for MLTA
        if (isMLTA && quoteData?.zipCode) {
            setValue("contactInformation.zipCode", quoteData.zipCode);
        }
    }, [parseResult, setValue, isMember, isMLTA, quoteData?.zipCode]);

    // Restore form data from Redux (runs after quote/offer effect, takes precedence)
    useEffect(() => {
        populateFormFromReduxData(contactDataRedux, setValue);
    }, [contactDataRedux, setValue]);

    // ========== Event Handlers ==========
    const handlePhoneTypeChange = useCallback(
        (value: string) => {
            setValue("contactInformation.phoneType", value as "Home" | "Mobile" | "Business");
            trigger("contactInformation.phoneType");

            if (shouldResetTCPAConsent(isMLTA, value)) {
                setValue("contactInformation.tcpaConsent", undefined);
            }
        },
        [setValue, trigger, isMLTA]
    );

    // ========== Ref Methods ==========
    const getContactValues = useCallback((): ContactInformationValues => {
        const formData = getValues("contactInformation");
        return {
            applicantType: formData.applicantType,
            firstName: formData.firstName,
            lastName: formData.lastName,
            dateOfBirth: formData.dateOfBirth,
            gender: formData.gender,
            email: formData.email,
            phone: formData.phone,
            phoneType: formData.phoneType,
            tcpaConsent: formData.tcpaConsent,
            address: formData.address,
            city: formData.city,
            state: toUsStates(formData.state)!,
            zipCode: formData.zipCode,
            aaaMemberNumber: formData.aaaMemberNumber,
        };
    }, [getValues]);

    useImperativeHandle(
        ref,
        () => ({
            validate: () => trigger("contactInformation"),
            getValues: getContactValues,
        }),
        [trigger, getContactValues]
    );

    // ========== Render ==========
    if (!parseResult.success || !parseResult.data.application) {
        return null;
    }

    return (
        <CardTemplate title="Your Contact Information" className={className}>
            <form className="flex flex-col gap-3" data-testid="contact-information-form" onSubmit={(e) => e.preventDefault()}>
                {showAaaMemberNumberSection && (
                    <AAAMemberField
                        register={register}
                        setValue={setValue}
                        name="contactInformation.aaaMemberNumber"
                        error={errors.contactInformation?.aaaMemberNumber?.message}
                        value={contactInformation?.aaaMemberNumber || ""}
                        id="contact-information-aaaMemberNumber"
                    />
                )}

                <div className="flex gap-2">
                    <FirstNameField
                        register={register("contactInformation.firstName")}
                        error={errors.contactInformation?.firstName?.message}
                        disabled={disableNames}
                        id="contact-information-firstName"
                    />
                    <LastNameField
                        register={register("contactInformation.lastName")}
                        error={errors.contactInformation?.lastName?.message}
                        disabled={disableNames}
                        id="contact-information-lastName"
                    />
                </div>

                {isGIWL && (
                    <GIWLQuoteModal
                        customTrigger={
                            <button
                                ref={editQuoteTriggerRef}
                                type="button"
                                style={{ display: "none" }}
                                tabIndex={-1}
                                aria-hidden
                            />
                        }
                    />
                )}
                <DateOfBirthField
                    monthRegister={register("contactInformation.dateOfBirth.month")}
                    dayRegister={register("contactInformation.dateOfBirth.day")}
                    yearRegister={register("contactInformation.dateOfBirth.year")}
                    monthValue={contactInformation?.dateOfBirth?.month}
                    dayValue={contactInformation?.dateOfBirth?.day}
                    yearValue={contactInformation?.dateOfBirth?.year}
                    isEditable={true}
                    disabled={true}
                    id="contact-information-dateOfBirth"
                    editNavigationPath={isGIWL ? undefined : "/quote"}
                    onEditClick={isGIWL ? () => editQuoteTriggerRef.current?.click() : undefined}
                />

                {(isMLTA || isGIWL) && (
                    <GenderRadioField
                        register={register("contactInformation.gender")}
                        error={errors.contactInformation?.gender?.message}
                        value={contactInformation?.gender}
                        maleId="contact-information-gender-male"
                        femaleId="contact-information-gender-female"
                        onChange={() => trigger("contactInformation.gender")}
                        isEditable={isGIWL}
                        id="contact-information-gender"
                        disabled={isGIWL}
                        onEditClick={isGIWL ? () => editQuoteTriggerRef.current?.click() : undefined}
                    />
                )}

                <EmailField
                    register={register("contactInformation.email")}
                    error={errors.contactInformation?.email?.message}
                    id="contact-information-email"
                />

                <div className="flex gap-3">
                    <PhoneNumberField
                        register={register("contactInformation.phone")}
                        error={errors.contactInformation?.phone?.message}
                        value={contactInformation?.phone || ""}
                        id="contact-information-phone"
                        width="flex-[3] min-w-0"
                    />

                    <PhoneTypeField
                        register={register("contactInformation.phoneType", {
                            onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                                handlePhoneTypeChange(e.target.value);
                            },
                        })}
                        error={errors.contactInformation?.phoneType?.message}
                        value={contactInformation?.phoneType}
                        id="contact-information-phoneType"
                        width="flex-[2] min-w-0"
                    />
                </div>

                {shouldShowTCPA && <TCPAConsentField register={register("contactInformation.tcpaConsent")} />}

                <AddressField
                    register={register("contactInformation.address")}
                    error={errors.contactInformation?.address?.message}
                    id="contact-information-address"
                    className="blur-upscope"
                />

                <CityField
                    register={register("contactInformation.city")}
                    error={errors.contactInformation?.city?.message}
                    id="contact-information-city"
                    className="blur-upscope"
                />

                <div className="flex gap-3">
                    <div className="flex-[2] min-w-0">
                        <StateField
                            register={register("contactInformation.state")}
                            error={errors.contactInformation?.state?.message}
                            value={contactInformation?.state}
                            isEditable={isMLTA || isGIWL}
                            disabled={true}
                            id="contact-information-state"
                            editNavigationPath={isGIWL ? undefined : "/quote"}
                            onEditClick={isGIWL ? () => editQuoteTriggerRef.current?.click() : undefined}
                            fullWidth
                            shouldNotShow={isGIWL ? ["NY"] : []}
                        />
                    </div>

                    <div className="flex-1 min-w-0">
                        <ZipCodeField
                            register={register("contactInformation.zipCode")}
                            error={errors.contactInformation?.zipCode?.message}
                            value={contactInformation?.zipCode}
                            isEditable={isMLTA}
                            disabled={isMLTA || isGIWL}
                            editNavigationPath={isGIWL ? undefined : "/quote"}
                            id="contact-information-zipCode"
                        />
                    </div>
                </div>

                {quoteData?.isMember && isGIWL && (
                    <AAAMemberField
                        register={register}
                        setValue={setValue}
                        name="contactInformation.aaaMemberNumber"
                        error={errors.contactInformation?.aaaMemberNumber?.message}
                        value={contactInformation?.aaaMemberNumber || ""}
                        id="contact-information-aaaMemberNumber"
                    />
                )}
            </form>
        </CardTemplate>
    );
};

ContactInformation.displayName = "ContactInformation";

export default ContactInformation;
